/**
 * 올린 HTML 콘텐츠(2026-10-19) — 콘솔에서 보여 주는 법.
 *
 * 서버가 주는 미리보기 주소(`preview_url` — `/api/v1/html-frames/…`, 2026-10-20)를 iframe 으로 그려 칸에 맞춰 줄인다. 그
 * 문서는 응답 머리로 출처 없는 샌드박스가 되고 화면과 같은 바깥 길(`HTML_CSP`)만 연다 — 스크립트가 돌아도 콘솔의 쿠키·
 * 저장소·화면에 닿지 못한다. iframe 에도 화면과 같은 `sandbox` 를 한 번 더 건다.
 *
 * 예전에는 글자를 받아 `srcdoc` 으로 넣었는데, 그 문서는 콘솔의 보안 정책(스크립트는 같은 출처의 파일만)을 물려받아
 * 스크립트로 그리는 콘텐츠가 빈 화면이었고, 목록·모니터링에는 아이콘만 그렸다. 주소로 받은 문서는 부모의 정책을 물려받지
 * 않는다.
 *
 * **화면 맞춤**(2026-10-20, `@signageone/core` 의 `html-fit.ts`): 고정 크기로 만든 HTML 은 기준 크기로 그려 화면에 맞춘다 —
 * 미리보기도 재생 화면과 같은 계산(`htmlFitBox`)으로 그린다. 적용할 화면을 골라 그 크기로 보고 맞춤을 고치는 일은
 * `HtmlScreenFit.tsx` 가 한다.
 *
 * 목록·모니터링 칸은 화면 가까이 온 칸만 띄운다. 누르거나 초점을 받지 않고, 소리는 내지 않는다(`allow="autoplay"` 를 주지
 * 않는다 — 소리는 화면에서 확인한다).
 *
 * **품은 영상**(2026-10-30, `@signageone/core` 의 `html-media.ts`): [화면 편집] 으로 넣은 영상은 문서에 주소가 없다. 화면처럼
 * 문서가 영상의 자리를 알리면(`HTML_MEDIA_REQUEST`) 칸 위에 겹친 층에 그 자리대로 그 영상의 콘솔 미리보기를 튼다(소리 없이).
 */
import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { FileCode } from 'lucide-react';
import {
  HTML_SANDBOX,
  htmlAreaBox,
  itemHtmlFit,
  readMediaRequest,
  type HtmlArea,
  type HtmlItemFit,
  type HtmlMediaBox,
} from '@signageone/core';
import type { Asset } from '../api/client.js';
import { useNearViewport } from './nearViewport.js';
import { mediaUrl } from './mediaSource.js';
import { htmlFrameSrc } from './htmlMeasure.js';

type Size = { width: number; height: number };

/** 화면을 모를 때 그리는 화면 — 가로 FHD. 목록·모니터링 칸은 16:9 다. */
export const DEFAULT_SCREEN: Size = { width: 1920, height: 1080 };

export { htmlFrameSrc };


/** 칸의 문서가 알린 영상의 자리와 그 주소 — 이 칸이 띄운 문서가 보낸 것만 받는다. 같은 칸이 다시 알리면 바꾼다. */
function useHtmlMedia(frame: RefObject<HTMLIFrameElement | null>): Array<HtmlMediaBox & { url: string }> {
  const [boxes, setBoxes] = useState<HtmlMediaBox[]>([]);
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const onMessage = (event: MessageEvent): void => {
      if (!frame.current?.contentWindow || event.source !== frame.current.contentWindow) return;
      const next = readMediaRequest(event.data);
      if (!next) return;
      setBoxes(next);
      for (const box of next) {
        void mediaUrl(box.id).then((url) => {
          if (url) setUrls((known) => (known[box.id] === url ? known : { ...known, [box.id]: url }));
        });
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frame]);
  return boxes.flatMap((box) => (urls[box.id] ? [{ ...box, url: urls[box.id]! }] : []));
}

/**
 * 칸 안에 화면을, 화면 안에 문서를 놓는 자리.
 *
 * 화면(`screen`)은 칸에 다 보이게(contain) 줄여 가운데 둔다. 문서는 기준 크기와 맞춤이 있으면 그 크기로 그려 화면에 맞추고
 * (`htmlFitBox` — 재생 화면과 같은 계산), 없으면 화면 크기 그대로 그린다. 문서의 자리는 화면 상자 기준이다.
 */
export function frameLayout(
  box: Size,
  screen: Size,
  design: Size | null,
  fit: HtmlItemFit | undefined,
  /** 문서에서 보일 영역(비율, 2026-11-03 반반 화면의 HTML 칸) — 없으면 전체. */
  area: HtmlArea | null = null,
): { screen: { scale: number; left: number; top: number }; frame: Size & { scale: number; left: number; top: number } } | null {
  if (box.width <= 0 || box.height <= 0) return null;
  const scale = Math.min(box.width / screen.width, box.height / screen.height);
  const outer = { scale, left: (box.width - screen.width * scale) / 2, top: (box.height - screen.height * scale) / 2 };
  if (design && fit) {
    const inner = htmlAreaBox(design, area, screen, fit);
    return {
      screen: outer,
      frame: { ...design, scale: scale * inner.scale, left: inner.left * scale, top: inner.top * scale },
    };
  }
  return { screen: outer, frame: { ...screen, scale, left: 0, top: 0 } };
}

/** 올린 HTML 을 칸에 맞춰 띄운다. 칸(`className`)이 크기를 정한다 — 자리 잡기(`relative`)는 여기서 한다. */
export function HtmlFrame({
  url,
  title,
  className,
  screen = DEFAULT_SCREEN,
  design = null,
  fit,
  area = null,
  cut = null,
}: {
  url: string;
  title: string;
  className: string;
  /** 그려 볼 화면의 크기 — 칸은 이 비율로 화면을 그린다. */
  screen?: Size;
  /** 기준 크기와 맞춤 — 둘 다 있을 때만 문서를 기준 크기로 그려 화면에 맞춘다. */
  design?: Size | null;
  fit?: HtmlItemFit;
  /** 문서에서 보일 영역(비율) — 기준 크기·맞춤이 있을 때만 쓴다. */
  area?: HtmlArea | null;
  /**
   * 화면 밖으로 나가 잘리는 쪽 — 그 가장자리에 붉은 띠를 두른다(2026-10-20). 미리보기는 화면에 보이는 것만 그려, 잘린 내용이
   * 있다는 것 자체가 그림에서는 보이지 않는다.
   */
  cut?: { bottom: boolean; right: boolean } | null;
}): ReactNode {
  const ref = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const near = useNearViewport(ref);
  const [box, setBox] = useState<Size>({ width: 0, height: 0 });
  const videos = useHtmlMedia(frameRef);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const layout = frameLayout(box, screen, design, fit, area);
  return (
    <div ref={ref} className={`${className} relative overflow-hidden`}>
      {near && layout ? (
        <div
          className="html-screen"
          style={{
            left: layout.screen.left,
            top: layout.screen.top,
            width: screen.width * layout.screen.scale,
            height: screen.height * layout.screen.scale,
          }}
        >
          <iframe
            ref={frameRef}
            src={htmlFrameSrc(url)}
            title={`${title} 미리보기`}
            sandbox={HTML_SANDBOX}
            referrerPolicy="no-referrer"
            loading="lazy"
            tabIndex={-1}
            aria-hidden="true"
            className="web-thumb-frame"
            style={{
              width: layout.frame.width,
              height: layout.frame.height,
              left: layout.frame.left,
              top: layout.frame.top,
              transform: `scale(${layout.frame.scale})`,
            }}
          />
          {videos.length > 0 ? (
            // 품은 영상 — 칸과 같은 크기·자리·배율의 층에, 문서가 알린 자리대로(`html-media.ts`).
            <div
              className="pointer-events-none absolute overflow-hidden"
              style={{
                width: layout.frame.width,
                height: layout.frame.height,
                left: layout.frame.left,
                top: layout.frame.top,
                transform: `scale(${layout.frame.scale})`,
                transformOrigin: '0 0',
              }}
              aria-hidden="true"
            >
              {videos.map((video, index) => (
                <video
                  key={`${index}:${video.id}`}
                  src={video.url}
                  muted
                  loop
                  autoPlay
                  playsInline
                  className="absolute"
                  style={{
                    left: video.x,
                    top: video.y,
                    width: video.width,
                    height: video.height,
                    objectFit: video.fit,
                    background: '#000',
                  }}
                />
              ))}
            </div>
          ) : null}
          {cut?.bottom ? (
            <span className="html-cut html-cut-bottom" aria-hidden="true">
              아래가 잘립니다
            </span>
          ) : null}
          {cut?.right ? (
            <span className="html-cut html-cut-right" aria-hidden="true">
              오른쪽이 잘립니다
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 콘텐츠의 기준 크기(고정 크기로 만든 HTML 만 — 늘고 주는 HTML 은 null). */
export function htmlDesign(asset: Pick<Asset, 'width' | 'height'>): Size | null {
  return asset.width && asset.height ? { width: asset.width, height: asset.height } : null;
}

/** 그림 자리. 주소가 있고 작은 칸이 아니면 내용을 줄여 띄우고(가로 FHD 화면에서처럼), 아니면 종류만. */
export function HtmlThumb({
  asset,
  className,
  compact = false,
}: {
  asset: Pick<Asset, 'preview_url' | 'title' | 'width' | 'height' | 'html_fit'>;
  className: string;
  compact?: boolean;
}): ReactNode {
  if (asset.preview_url && !compact) {
    return (
      <div className={`${className} relative`}>
        <HtmlFrame
          url={asset.preview_url}
          title={asset.title}
          className="h-full w-full bg-black"
          design={htmlDesign(asset)}
          fit={itemHtmlFit(asset.html_fit, asset.width, asset.height)}
        />
        <span className="media-badge is-html" aria-hidden="true">
          <FileCode className="media-badge-icon" />
          <span>HTML</span>
        </span>
      </div>
    );
  }
  return (
    <div className={`${className} flex flex-col items-center justify-center gap-1 bg-slate-800 text-slate-300`}>
      <FileCode className={compact ? 'h-4 w-4' : 'h-7 w-7'} strokeWidth={1.6} />
      {compact ? null : <span className="text-[11px] font-medium tracking-wide">HTML</span>}
    </div>
  );
}

/** 잰 결과를 사람 말로 — 무엇이 감지됐고 화면에서 어떻게 나가는가. */
export function htmlFitSummary(asset: Pick<Asset, 'width' | 'height' | 'html_fit' | 'html_measured_at' | 'html_size_manual'>): string {
  if (asset.html_measured_at === null || asset.html_measured_at === undefined) return '아직 크기를 재지 않았습니다';
  const design = htmlDesign(asset);
  if (!design) return '화면 크기에 맞춰 늘고 주는 HTML — 어느 화면에서나 그대로 띄웁니다';
  const source = asset.html_size_manual ? '직접 정함' : '자동 감지';
  const how =
    asset.html_fit === 'none'
      ? '화면 크기 그대로 띄웁니다'
      : asset.html_fit === 'cover'
        ? '화면을 꽉 채웁니다(넘치는 쪽은 잘림)'
        : '화면에 다 보이게 줄이거나 늘립니다';
  return `고정 크기 ${design.width}×${design.height}(${source}) — ${how}`;
}
